import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';

interface Application {
  id: string;
  name: string;
  email: string;
  company: string;
  phone: string;
  message: string;
  notes?: string;
  createdAt: {
    toDate: () => Date;
  };
}

const Admin = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const { currentUser, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser || !isAdmin) return;

    const q = query(
      collection(db, 'applications'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const applicationsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Application[];
      
      setApplications(applicationsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser, isAdmin]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Hiba történt a kijelentkezés során:', error);
    }
  };

  const handleSaveNote = async (applicationId: string) => {
    try {
      await updateDoc(doc(db, 'applications', applicationId), {
        notes: noteText
      });
      setEditingNote(null);
      setNoteText('');
    } catch (error) {
      console.error('Hiba történt a megjegyzés mentése során:', error);
    }
  };

  if (!currentUser || !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="flex-grow container mx-auto px-4 py-8">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4">Adatok betöltése...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-800 rounded-xl shadow-lg mb-8 p-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Admin Panel</h1>
            <p className="text-purple-200">Beérkezett jelentkezések kezelése</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Kijelentkezés
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dátum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Név
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cég
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefon
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Üzenet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Megjegyzések
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applications.map((application) => (
                <tr key={application.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {application.createdAt.toDate().toLocaleDateString('hu-HU')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {application.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <a href={`mailto:${application.email}`} className="text-purple-600 hover:text-purple-900">
                      {application.email}
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {application.company}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <a href={`tel:${application.phone}`} className="text-purple-600 hover:text-purple-900">
                      {application.phone}
                    </a>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {application.message}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {editingNote === application.id ? (
                      <div className="flex items-center space-x-2">
                        <textarea
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          className="w-full px-2 py-1 border rounded-md"
                          rows={3}
                        />
                        <button
                          onClick={() => handleSaveNote(application.id)}
                          className="px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                        >
                          Mentés
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span>{application.notes || 'Nincs megjegyzés'}</span>
                        <button
                          onClick={() => {
                            setEditingNote(application.id);
                            setNoteText(application.notes || '');
                          }}
                          className="px-2 py-1 text-purple-600 hover:text-purple-900"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
};

export default Admin; 